import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { API_URL } from "../../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Ticket, Copy, Ban, Plus } from "lucide-react";

const KEY_STATUS = {
  pending: { label: "Pendente", cls: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  used: { label: "Usada", cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  revoked: { label: "Revogada", cls: "bg-red-500/20 text-red-400 border-red-500/30" },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-PT") : "—");

export const KeysTab = ({ headers, prefillCompany }) => {
  const [keys, setKeys] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ company_id: "all", plan: "bronze", billing_cycle: "monthly", note: "" });
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [kRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/backoffice/keys`, { headers }),
        axios.get(`${API_URL}/backoffice/companies`, { headers }),
      ]);
      setKeys(kRes.data);
      setCompanies(cRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao carregar chaves"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (prefillCompany?.company_id) setForm((f) => ({ ...f, company_id: prefillCompany.company_id }));
  }, [prefillCompany]);

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(
      () => toast.success(`Chave ${code} copiada`),
      () => toast.error("Não foi possível copiar")
    );
  };

  const createKey = async () => {
    setCreating(true);
    try {
      const payload = {
        plan: form.plan,
        billing_cycle: form.billing_cycle,
        company_id: form.company_id === "all" ? null : form.company_id,
        note: form.note || null,
      };
      const res = await axios.post(`${API_URL}/backoffice/keys`, payload, { headers });
      setCreatedKey(res.data);
      toast.success("Chave de ativação criada");
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar chave"); }
    finally { setCreating(false); }
  };

  const revokeKey = async (key) => {
    try {
      await axios.put(`${API_URL}/backoffice/keys/${key.key_id}/revoke`, {}, { headers });
      toast.success(`Chave ${key.code} revogada`);
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao revogar"); }
  };

  const filtered = filter === "all" ? keys : keys.filter((k) => k.status === filter);

  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/[0.04] p-5 space-y-4" data-testid="key-create-card">
        <div className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-cyan-400" />
          <p className="font-bold">Gerar Chave de Ativação</p>
        </div>
        <p className="text-sm text-white/50">
          Após receber o pagamento, gere a chave e envie ao cliente (WhatsApp/email). O cliente insere-a na página
          <strong className="text-white/70"> "O Meu Plano"</strong> e a subscrição ativa automaticamente: mensal = 30 dias, anual = 365 dias.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Empresa destino</Label>
            <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger className="bg-white/5 border-white/15 text-white" data-testid="key-company-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">Qualquer empresa (chave livre)</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Plano</Label>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger className="bg-white/5 border-white/15 text-white" data-testid="key-plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bronze">LUMORA Bronze</SelectItem>
                <SelectItem value="ouro">LUMORA Ouro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Ciclo</Label>
            <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
              <SelectTrigger className="bg-white/5 border-white/15 text-white" data-testid="key-cycle-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensal (30 dias)</SelectItem>
                <SelectItem value="annual">Anual (365 dias)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70 text-xs">Nota (opcional)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Ex: comprovativo BAI 12/06" className="bg-white/5 border-white/15 text-white placeholder:text-white/25" data-testid="key-note-input" />
          </div>
        </div>
        <Button className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold" disabled={creating} onClick={createKey} data-testid="create-key-btn">
          <Plus className="w-4 h-4 mr-1.5" /> {creating ? "A gerar..." : "Gerar Chave"}
        </Button>

        {createdKey && (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="created-key-result">
            <div>
              <p className="text-xs text-emerald-300/80 mb-1">Chave gerada — envie ao cliente:</p>
              <p className="text-2xl font-black font-mono tracking-wider" data-testid="created-key-code">{createdKey.code}</p>
              <p className="text-xs text-white/50 mt-1">
                {createdKey.plan === "ouro" ? "LUMORA Ouro" : "LUMORA Bronze"} · {createdKey.billing_cycle === "annual" ? "Anual (365 dias)" : "Mensal (30 dias)"}
                {createdKey.company_name ? ` · só para ${createdKey.company_name}` : " · qualquer empresa"}
              </p>
            </div>
            <Button variant="outline" className="border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/20" onClick={() => copyCode(createdKey.code)} data-testid="copy-created-key-btn">
              <Copy className="w-4 h-4 mr-1.5" /> Copiar
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          {[["all", "Todas"], ["pending", "Pendentes"], ["used", "Usadas"], ["revoked", "Revogadas"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${filter === v ? "bg-cyan-500 text-black" : "bg-white/5 text-white/50 hover:text-white/80"}`}
              data-testid={`key-filter-${v}`}
            >
              {l} ({v === "all" ? keys.length : keys.filter((k) => k.status === v).length})
            </button>
          ))}
        </div>

        {filtered.map((k) => {
          const st = KEY_STATUS[k.status] || KEY_STATUS.pending;
          return (
            <div key={k.key_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-3" data-testid={`key-row-${k.key_id}`}>
              <div className="flex items-center gap-2 min-w-[200px]">
                <span className="font-mono font-bold">{k.code}</span>
                <button onClick={() => copyCode(k.code)} className="text-white/40 hover:text-white" data-testid={`copy-key-${k.key_id}`} aria-label="Copiar chave">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
              <Badge className={k.plan === "ouro" ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30" : "bg-amber-600/20 text-amber-500 border-amber-600/30"}>
                {k.plan === "ouro" ? "Ouro" : "Bronze"} · {k.billing_cycle === "annual" ? "Anual" : "Mensal"}
              </Badge>
              <Badge className={st.cls}>{st.label}</Badge>
              <span className="text-xs text-white/40 flex-1 min-w-[160px]">
                {k.company_name ? `Para: ${k.company_name}` : "Qualquer empresa"}
                {k.note ? ` · ${k.note}` : ""}
                {k.status === "used" ? ` · usada por ${k.used_by_company_name || "—"} em ${fmtDate(k.used_at)}` : ` · criada ${fmtDate(k.created_at)}`}
              </span>
              {k.status === "pending" && (
                <Button size="sm" variant="outline" className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10 ml-auto" onClick={() => revokeKey(k)} data-testid={`revoke-key-${k.key_id}`}>
                  <Ban className="w-3.5 h-3.5 mr-1" /> Revogar
                </Button>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <p className="text-center text-white/40 py-8">Nenhuma chave nesta categoria</p>}
      </div>
    </div>
  );
};
